export function MarketConditionsImpact() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How Market Conditions Impact Your Property's Value
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Market conditions play a crucial role in apartment building valuation. Understanding current market dynamics helps you time your sale strategically and set realistic price expectations.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/f5ea77b882431203f4676a2606f0a815.jpg"
              alt="Real estate market conditions and trends"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Market Cycles</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Real estate markets move in cycles, alternating between seller's markets (high demand, low supply) and buyer's markets (low demand, high supply). Your property's value can vary by 20-30% depending on where we are in the cycle.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              In strong seller's markets, cap rates compress (decrease), driving values higher. In buyer's markets, cap rates expand (increase), pushing values lower. Understanding these dynamics helps you make informed decisions about timing your sale.
            </p>
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
                <h4 className="font-semibold text-gray-900 mb-1">Seller's Market</h4>
                <p className="text-gray-600">High demand, multiple offers, compressed cap rates, premium pricing</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-600">
                <h4 className="font-semibold text-gray-900 mb-1">Balanced Market</h4>
                <p className="text-gray-600">Moderate demand, fair pricing, stable cap rates, reasonable negotiations</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
                <h4 className="font-semibold text-gray-900 mb-1">Buyer's Market</h4>
                <p className="text-gray-600">Low demand, extended marketing time, expanded cap rates, discounted pricing</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Market Indicators to Watch</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-line-chart-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Cap Rate Trends</h4>
              <p className="text-gray-600 leading-relaxed">
                Falling cap rates indicate rising values and strong demand. Rising cap rates signal weakening market conditions and declining values.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-time-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Days on Market</h4>
              <p className="text-gray-600 leading-relaxed">
                Properties selling quickly (30-60 days) indicate strong markets. Extended marketing times (120+ days) suggest buyer's market conditions.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-home-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Inventory Levels</h4>
              <p className="text-gray-600 leading-relaxed">
                Low inventory (3-6 months supply) favors sellers. High inventory (9+ months) favors buyers and puts downward pressure on prices.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-percent-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Interest Rates</h4>
              <p className="text-gray-600 leading-relaxed">
                Rising interest rates reduce buyer purchasing power and lower property values. Falling rates increase demand and boost values.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-building-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">New Construction</h4>
              <p className="text-gray-600 leading-relaxed">
                Heavy new construction increases supply and can depress values. Limited new supply supports higher pricing for existing properties.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-briefcase-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Employment Growth</h4>
              <p className="text-gray-600 leading-relaxed">
                Strong job growth drives rental demand and supports higher property values. Job losses weaken rental markets and reduce values.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Timing Your Sale for Maximum Value</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              While you can't always control when you need to sell, understanding market timing can help you maximize value when possible. Here are strategies for different market conditions:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-green-600">
                <h4 className="font-bold text-gray-900 mb-2">Strong Market Strategy</h4>
                <p className="text-gray-600">Price aggressively, create urgency, leverage multiple offers, minimize contingencies for quick closing</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-yellow-600">
                <h4 className="font-bold text-gray-900 mb-2">Balanced Market Strategy</h4>
                <p className="text-gray-600">Price at market value, highlight property strengths, be flexible on terms, respond quickly to offers</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-red-600">
                <h4 className="font-bold text-gray-900 mb-2">Weak Market Strategy</h4>
                <p className="text-gray-600">Price competitively, offer seller financing, consider lease options, focus on motivated cash buyers</p>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=strategic-timing-calendar-planning-optimal-timing-real-estate-sale-timing-strategy-market-timing-decision-making-strategic-planning-professional-business-timing-clock-calendar-concept&width=800&height=600&seq=timing-strategy-001&orientation=landscape"
              alt="Strategic timing for apartment building sale"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}